import LockIcon from '@mui/icons-material/Lock';
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Alert, CircularProgress, Divider
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Sidebar, { SIDEBAR_WIDTH } from '../components/Sidebar';
import { useIsMobile } from '../hooks/useIsMobile';
import api from '../api/axios';

export default function SettingsPage() {
  const isMobile = useIsMobile();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [mfaEnforcement, setMfaEnforcement] = useState(false);
  const [mfaEnforcementLoading, setMfaEnforcementLoading] = useState(false);
  const [form, setForm] = useState({
    work_start_hour: 8,
    work_start_minute: 0,
    work_end_hour: 16,
    work_end_minute: 0,
    lateness_threshold_hour: 10,
    lateness_threshold_minute: 0,
  });

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
  setLoading(true);
  try {
    const [wtRes, mfaRes] = await Promise.all([
      api.get('/settings/work-time/'),
      api.get('/auth/mfa-enforcement/'),
    ]);
    setSettings(wtRes.data);
    setForm({
      work_start_hour: wtRes.data.work_start_hour,
      work_start_minute: wtRes.data.work_start_minute,
      work_end_hour: wtRes.data.work_end_hour,
      work_end_minute: wtRes.data.work_end_minute,
      lateness_threshold_hour: wtRes.data.lateness_threshold_hour,
      lateness_threshold_minute: wtRes.data.lateness_threshold_minute,
      });
      setMfaEnforcement(mfaRes.data.enforce_mfa_for_all);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMFAEnforcement = async () => {
    setMfaEnforcementLoading(true);
    try {
      const { data } = await api.post('/auth/mfa-enforcement/', {
        enforce_mfa_for_all: !mfaEnforcement,
      });
      setMfaEnforcement(data.enforce_mfa_for_all);
      setMsg({ type: 'success', text: data.message });
    } catch {
      setMsg({ type: 'error', text: 'Failed to update MFA enforcement.' });
    } finally {
      setMfaEnforcementLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/settings/work-time/', form);
      setSettings(data);
      setMsg({ type: 'success', text: 'Work time settings saved successfully.' });
    } catch {
      setMsg({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (hour, minute) => {
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${h}:${m} (${displayHour}:${m} ${ampm})`;
  };

  const TimeField = ({ label, hourKey, minuteKey }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 1, color: '#444' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <TextField
          label="Hour"
          type="number"
          size="small"
          value={form[hourKey]}
          onChange={(e) => {
            const val = Math.min(23, Math.max(0, parseInt(e.target.value) || 0));
            setForm({ ...form, [hourKey]: val });
          }}
          inputProps={{ min: 0, max: 23 }}
          sx={{ width: 90 }}
        />
        <Typography sx={{ fontSize: 18, color: '#888' }}>:</Typography>
        <TextField
          label="Minute"
          type="number"
          size="small"
          value={form[minuteKey]}
          onChange={(e) => {
            const val = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
            setForm({ ...form, [minuteKey]: val });
          }}
          inputProps={{ min: 0, max: 59 }}
          sx={{ width: 90 }}
        />
        <Typography sx={{ fontSize: 12, color: '#888' }}>
          = {formatTime(form[hourKey], form[minuteKey])}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{
        ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
        p: isMobile ? 2 : 3,
        pt: isMobile ? 7 : 3,
        minHeight: '100vh',
        flex: 1,
        minWidth: 0,
        overflowX: 'hidden',
      }}>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Settings</Typography>
          <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
        </Box>

        {msg.text && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress sx={{ color: '#1a4d2e' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

            {/* Work time settings card */}
            <Card>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: '#1a4d2e' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Work Time Configuration</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 12, color: '#888', mb: 3 }}>
                  These settings apply to all employees. Changes take effect immediately on the next clock-in.
                </Typography>

                <TimeField
                  label="Work Start Time"
                  hourKey="work_start_hour"
                  minuteKey="work_start_minute"
                />
                <TimeField
                  label="Work End Time (Closing Time)"
                  hourKey="work_end_hour"
                  minuteKey="work_end_minute"
                />
                <Divider sx={{ my: 2 }} />
                <TimeField
                  label="Lateness Threshold (clock-in after this time = late)"
                  hourKey="lateness_threshold_hour"
                  minuteKey="lateness_threshold_minute"
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
                  sx={{ mt: 1, background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
                >
                  Save settings
                </Button>
              </CardContent>
            </Card>

            {/* Current settings summary card */}
            <Card sx={{ alignSelf: 'start' }}>
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Current Configuration</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                {settings && [
                  { label: 'Work starts at', value: formatTime(settings.work_start_hour, settings.work_start_minute) },
                  { label: 'Work ends at', value: formatTime(settings.work_end_hour, settings.work_end_minute) },
                  { label: 'Late after', value: formatTime(settings.lateness_threshold_hour, settings.lateness_threshold_minute) },
                  { label: 'Last updated', value: new Date(settings.updated_at).toLocaleString() },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '0.5px solid #f0f4f0' }}>
                    <Typography sx={{ fontSize: 13, color: '#888' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography>
                  </Box>
                ))}

                <Box sx={{ mt: 2.5, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
                  <Typography sx={{ fontSize: 12, color: '#888', fontWeight: 500, mb: 0.5 }}>
                    How this works
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                    • Employees cannot clock in after work end time
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                    • Clock-in after lateness threshold is flagged as late
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                    • Clock-out before work end time requires a reason
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                    • Work days are Monday to Friday only
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            {/* MFA enforcement card */}
<Card sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8', display: 'flex', alignItems: 'center', gap: 1 }}>
    <LockIcon sx={{ fontSize: 16, color: '#1a4d2e' }} />
    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>MFA Enforcement</Typography>
  </Box>
  <CardContent sx={{ p: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.5 }}>
          Enforce MFA for all users
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#888', mb: 1 }}>
          When enabled, all users are required to use two-factor authentication
          on every login, regardless of their individual MFA preference.
        </Typography>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 0.75, borderRadius: 2,
          background: mfaEnforcement ? '#eaf3de' : '#f1efe8',
        }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            background: mfaEnforcement ? '#2d7a4f' : '#aaa',
          }} />
          <Typography sx={{
            fontSize: 12, fontWeight: 500,
            color: mfaEnforcement ? '#27500a' : '#666',
          }}>
            {mfaEnforcement ? 'Enforcement is ON — all users must use MFA' : 'Enforcement is OFF — MFA is optional per user'}
          </Typography>
        </Box>
      </Box>
      <Button
        variant={mfaEnforcement ? 'outlined' : 'contained'}
        onClick={handleToggleMFAEnforcement}
        disabled={mfaEnforcementLoading}
        sx={mfaEnforcement
          ? { borderColor: '#791f1f', color: '#791f1f', borderRadius: 2, minWidth: 160 }
          : { background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2, minWidth: 160 }
        }
      >
        {mfaEnforcementLoading
          ? <CircularProgress size={18} color="inherit" />
          : mfaEnforcement ? 'Disable enforcement' : 'Enable enforcement'
        }
      </Button>
    </Box>
            </CardContent>
          </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
}